/**
 * Offline bootstrap
 *
 * Binds the on-device store to the signed-in account and installs the sync
 * triggers, mirroring `playbackSyncBootstrap.js` — same shape, same
 * idempotency, same sign-out wiring.
 *
 * Idempotent per `sub`: a second call from the same account is a no-op, and an
 * account switch rebinds the store so no data crosses between accounts
 * (FR-027).
 */

import { getAccessToken, decodeToken, registerSignOutCleanup } from '../auth';
import { wipeAccount } from './db';
import * as repository from './repository';
import { evictChapter, retryPending } from './audioCache';
import { publishAudioCacheName, clearAudioCacheName } from './swHandshake';
import { sync, hydrate, registerSessionRejectedHandler, registerPushDrain } from './syncEngine';
import { getPlaybackSyncProvider } from '../playbackSyncBootstrap';
import { installSyncTriggers } from './syncTriggers';

let activeSub = null;
let uninstallTriggers = null;

const getCurrentUserSub = () => {
  const token = getAccessToken();
  if (!token) return null;
  const decoded = decodeToken(token);
  return decoded && typeof decoded.sub === 'string' ? decoded.sub : null;
};

export const bootstrapOffline = () => {
  const sub = getCurrentUserSub();
  if (!sub || activeSub === sub) return;
  try {
    if (uninstallTriggers) uninstallTriggers();
    activeSub = sub;
    repository.setAccount(sub);

    // Deleting a project must reclaim its audio, not just its metadata
    // (FR-017). The repository holds the cascade; this supplies the file half.
    repository.registerAudioEvictor(evictChapter);

    // The worker has no session, so it cannot derive the account-namespaced
    // audio cache name. Tell it.
    publishAudioCacheName(repository.audioCacheForAccount());

    // The server rejecting the session is the one case that clears the device
    // (FR-027). Being offline with a lapsed token is not (FR-025).
    registerSessionRejectedHandler(async () => {
      await wipeAccount(sub);
      repository.clearAccount();
    });

    // Sign-out wipes everything this account left on the device (FR-026).
    registerSignOutCleanup(() => {
      const doomed = activeSub;
      activeSub = null;
      repository.clearAccount();
      clearAudioCacheName();
      return wipeAccount(doomed);
    });

    // Push pending playback writes ahead of every pull (FR-009 / FR-010). The
    // provider is resolved lazily because playback bootstrap may not have run
    // yet when this is registered.
    registerPushDrain(() => {
      const provider = getPlaybackSyncProvider();
      return provider ? provider.drainAll() : Promise.resolve(0);
    });

    hydrate().then(() => {
      uninstallTriggers = installSyncTriggers(sync, () => {
        // Downloads interrupted by the outage resume without the user asking
        // again (FR-020).
        retryPending().catch((e) => console.warn('OfflineBootstrap: retry failed', e));
      });
    });
  } catch (e) {
    console.warn('OfflineBootstrap failed:', e);
  }
};

// Test-only: reset module state between cases.
export const _resetOfflineBootstrap = () => {
  if (uninstallTriggers) uninstallTriggers();
  uninstallTriggers = null;
  activeSub = null;
};
